import { useState, useRef, useEffect } from "react";
import Loading from "./Loading";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const BetsTable = ({ eventBets, isLoading, currentPage = 1, onPageChange }) => {
  const [activeTab, setActiveTab] = useState("match");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef(null);

  const matchBets   = eventBets?.data?.bookmaker || [];
  const sessionBets = eventBets?.data?.fancy     || [];
  const tossBets    = eventBets?.data?.toss      || [];
  const counts      = eventBets?.counts || { bookmaker: 0, fancy: 0, toss: 0 };
  const pagination  = eventBets?.pagination || {};

  const getActiveTotalPages = () => {
    if (activeTab === "session") return pagination.fancy?.totalPages || 0;
    if (activeTab === "toss")    return pagination.toss?.totalPages  || 0;
    return pagination.bookmaker?.totalPages || 0;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowSearchDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getActiveBets = () => {
    if (activeTab === "session") return sessionBets;
    if (activeTab === "toss")    return tossBets;
    return matchBets;
  };

  const activeBets = getActiveBets();

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2,'0');
    const sec = String(d.getSeconds()).padStart(2,'0');
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${String(h).padStart(2,'0')}:${min}:${sec} ${ampm}`;
  };

  const tabs = [
    { key: "match",   label: "Match",   count: counts.bookmaker },
    { key: "session", label: "Session", count: counts.fancy },
    { key: "toss",    label: "Toss",    count: counts.toss || 0 },
  ];

  return (
    <div className="bets-table bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-4 py-2.5 flex items-center gap-2.5">
        <div className="w-1 h-5 bg-blue-400 rounded-full" />
        <span className="text-white font-semibold text-sm tracking-wide">Bets</span>
      </div>

      {/* Tabs */}
      <div className="flex bg-white px-3 gap-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); onPageChange?.(1); }}
            className={`px-3 py-2 my-1 text-xs font-semibold rounded-md transition-all ${
              activeTab === tab.key
                ? 'bg-teal-500 text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}
          >
            {tab.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === tab.key ? 'bg-white text-teal-600' : 'bg-slate-200 text-slate-500'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loading />
          </div>
        ) : activeBets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <svg className="w-10 h-10 mb-2 text-slate-300" viewBox="0 0 64 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="32" cy="33" rx="32" ry="7" fill="#e2e8f0"/>
              <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#cbd5e1"/>
              <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#cbd5e1"/>
            </svg>
            <p className="text-xs font-medium">No bets found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#0F2327] text-white">
                {activeTab === "session" && <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Volume</th>}
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Amount</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Rate</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Type</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Team</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    Client
                    <div ref={dropdownRef} className="inline-block relative">
                      <button onClick={() => setShowSearchDropdown(!showSearchDropdown)} className="text-white/70 hover:text-white">
                        <SearchIcon />
                      </button>
                      {showSearchDropdown && (
                        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-50 min-w-[200px] animate-slideDown">
                          <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search client..."
                            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 mb-2"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button onClick={() => { setSearchValue(''); setShowSearchDropdown(false); }}
                              className="flex-1 px-2 py-1.5 bg-slate-100 text-slate-600 text-xs rounded-lg hover:bg-slate-200">
                              Cancel
                            </button>
                            <button onClick={() => setShowSearchDropdown(false)}
                              className="flex-1 px-2 py-1.5 bg-[#0c1524] text-white text-xs rounded-lg hover:bg-[#162032]">
                              Search
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Time</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Loss</th>
                <th className="px-2 py-2.5 text-left font-bold uppercase tracking-wider whitespace-nowrap">Profit</th>
              </tr>
            </thead>
            <tbody>
              {activeBets.map((bet, index) => {
                const isLagaiOrYes = ["lagai", "yes", "back"].includes(bet.type?.toLowerCase());
                return (
                  <tr key={bet.betId || index} className={`border-b border-slate-100 ${isLagaiOrYes ? 'bet-row-back' : 'bet-row-lay'}`}>
                    {activeTab === "session" && <td className="px-2 py-2 text-sm font-semibold whitespace-nowrap text-slate-600">{bet.size || '-'}</td>}
                    <td className="px-2 py-2 text-sm font-semibold text-slate-800">{bet.amount || '-'}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700">{bet.rate || '-'}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        isLagaiOrYes ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                      }`}>
                        {bet.type || '-'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700 whitespace-nowrap">{bet.team || bet.selectionName || '-'}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-700 whitespace-nowrap">
                      <div>{bet.client || '-'}</div>
                      {bet.fullName && <div className="text-xs text-slate-500 font-medium">{bet.fullName}</div>}
                    </td>
                    <td className="px-2 py-2 text-sm font-semibold text-slate-500 whitespace-nowrap">{formatTime(bet.date || bet.createdAt)}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-red-500">{(bet.loss || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-sm font-semibold text-emerald-600">{(bet.profit || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {getActiveTotalPages() > 1 && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            Page {currentPage} of {getActiveTotalPages()}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= getActiveTotalPages()}
              className="px-3 py-1 text-xs font-semibold rounded-md bg-[#0c1524] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#162032] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetsTable;
